import styles from './RelatedComparisons.module.css'
import SectionHeader from './SectionHeader'
import Carousel from './Carousel'
import HighlightText from 'components/common/HighlightText'
import type { DealHuntVersus } from 'lib/dealhunt/types'
import { useCatalogEvent } from 'lib/analytics/catalog'

interface RelatedComparisonsProps {
  productName: string
  productImageUrl: string | null
  versus: DealHuntVersus[]
}

/**
 * "Related comparisons" (versus) carousel — real SoundGuys head-to-heads from
 * DealHunt's `enrichment.versus` (oriented from this product's POV; newest first).
 * The template only mounts this section when there's at least one comparison.
 */
const RelatedComparisons: React.VFC<RelatedComparisonsProps> = ({
  productName,
  productImageUrl,
  versus,
}) => {
  const { track } = useCatalogEvent()
  return (
    <section className={styles.section}>
      <SectionHeader
        title="Related comparisons"
        link={{ href: 'https://www.soundguys.com/versus/', label: 'Browse all versus ›' }}
        intro={`See how the ${productName} stacks up against the competition.`}
      />

      <Carousel>
        {versus.map((v, i) => (
          <a
            key={v.postId}
            className={styles.card}
            href={v.url}
            target="_blank"
            rel="noopener noreferrer"
            title={v.title}
            onClick={() =>
              track('product_page_versus_click', {
                target_id: v.postId,
                target_title: v.title,
                target_product_name: v.opponent.name,
                destination_url: v.url,
                position: i + 1,
              })
            }
          >
            <span className={styles.banner}>
              <span className={styles.side}>
                {(v.self?.imageUrl ?? productImageUrl) && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    className={styles.sideImg}
                    src={v.self?.imageUrl ?? productImageUrl ?? undefined}
                    alt={productName}
                  />
                )}
                <span className={styles.sideTag}>{productName}</span>
              </span>
              <span className={`${styles.side} ${styles.sideRight}`}>
                {v.opponent.imageUrl && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img className={styles.sideImg} src={v.opponent.imageUrl} alt={v.opponent.name} />
                )}
                <span className={styles.sideTag}>{v.opponent.name}</span>
              </span>
              <span className={styles.vs}>{'VS'}</span>
            </span>
            <span className={styles.body}>
              <span className={styles.eyebrow}>{'Versus'}</span>
              <span className={styles.title}>
                <HighlightText
                  text={`${productName} vs ${v.opponent.name}`}
                  query={productName}
                  exact
                />
              </span>
              {v.subtitle && <span className={styles.tag}>{v.subtitle}</span>}
              <span className={styles.cta}>{'Read comparison ›'}</span>
            </span>
          </a>
        ))}
      </Carousel>
    </section>
  )
}

export default RelatedComparisons
